import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PapiComment } from '@wix/da-papi-types';
import { EditingState } from '../../redux/types';
import { isCommentReduxReady } from '../../redux/selectors';
import { isCommentHidden } from '../../utils';
import { CommentContext, CommentContextValues } from '../CommentContext';

export interface CommentContextProviderProps {
  comment: PapiComment;
  hasReplyDraft: boolean;
  onEditingStateChange?: (state: EditingState) => void;
  extraActions?: CommentContextValues['extraActions'];
  children: React.ReactNode;
}

export const CommentContextProvider: React.FC<CommentContextProviderProps> = ({
  children,
  comment,
  hasReplyDraft,
  onEditingStateChange,
  extraActions,
}) => {
  const isReduxReady = useSelector(isCommentReduxReady);
  const [editingState, setEditingState] = useState<EditingState>('none');
  const [isPeeking, setPeeking] = useState(false);
  const [canShowActionMenu, setCanShowActionMenu] = useState(true);

  const isCommentVisible = !isCommentHidden(comment, isPeeking);

  const contextState: CommentContextValues = useMemo(
    () => ({
      comment,
      isEditing: editingState === 'editing',
      isReplying: editingState === 'replying',
      isPeeking,
      isReduxReady,
      canShowActionMenu,
      isCommentVisible,
      setEditingState: (state: EditingState) => {
        setEditingState(state);
        if (onEditingStateChange) {
          onEditingStateChange(state);
        }
      },
      extraActions,
      setPeeking,
      setCanShowActionMenu,
    }),
    [
      comment,
      editingState,
      isPeeking,
      isReduxReady,
      isCommentVisible,
      canShowActionMenu,
      onEditingStateChange,
      extraActions,
    ]
  );

  // If there is a reply draft saved, change the editing state to "replying", which
  // will shown a new post box and will in turn restore the saved editor state.
  // This draft is saved on local storage if the user navigates away or closes a page
  // after having written an unsent comment.
  useEffect(() => {
    if (hasReplyDraft) {
      setEditingState('replying');
    }
  }, [hasReplyDraft]);

  return <CommentContext.Provider value={contextState}>{children}</CommentContext.Provider>;
};
CommentContextProvider.displayName = 'CommentContextProvider';
